import { DepositPaymentEvent, OrderState, PaymentEventEnum, useApiTradeV1TradeCompaniesPaymentTermsList } from 'api';
import {
  BooleanSelectField,
  CompanyPaymentTermsAutocomplete,
  createPaymentTermLabel,
  PaymentTermOption,
  PriceAndCurrencyField,
  SelectField,
  type SelectOption,
  SwitchField,
  TextField,
  useFormContext,
  useResetOnChange,
  useWatch,
} from 'form';
import { useCallback, useContext, useEffect, useState } from 'react';
import { Box, SectionContainer } from 'ui';

import { PurchaseOrderFormContext } from '~/orders/contexts/PurchaseOrderFormContext';
import { useVariablePricingEnabled } from '~/orders/hooks';

import { CreateFormValues } from '../../pages/PurchaseOrderCreate/PurchaseOrderCreate';
import { VariablePricingFields } from '..';
import { PaymentMethod } from '../PaymentMethod/PaymentMethod';

const DEPOSIT_PAYMENT_EVENT_OPTIONS: SelectOption[] = [
  {
    label: 'Upon receipt of signed PO',
    value: DepositPaymentEvent.SIGNED_PO,
  },
  {
    label: 'Upon receipt of signed PO and successful Pre-inspection',
    value: DepositPaymentEvent.SIGNED_PO_AND_PREINSPECTION,
  },
  {
    label: 'Collection from Supplier',
    value: DepositPaymentEvent.COLLECTION,
  },
  {
    label: 'After successful Pre-inspection + Collection from Supplier',
    value: DepositPaymentEvent.PREINSPECTION_AND_COLLECTION,
  },
];

const PAYMENT_EVENT_OPTIONS: SelectOption[] = [
  {
    label: 'Collection from supplier',
    value: PaymentEventEnum.CL,
  },
  {
    label: 'Export',
    value: PaymentEventEnum.XP,
  },
  {
    label: 'Bill of Lading',
    value: PaymentEventEnum.BL,
  },
  {
    label: 'Destination port arrival',
    value: PaymentEventEnum.DA,
  },
  {
    label: 'ICD arrival',
    value: PaymentEventEnum.IA,
  },
  {
    label: 'Site Delivery',
    value: PaymentEventEnum.SD,
  },
];

type Props = {
  isBuyer: boolean;
  currencyPriceName: string;
};

export const FinancialDetailsSection = ({ currencyPriceName }: Props) => {
  const { setValue, resetField } = useFormContext<CreateFormValues>();
  const { variant, defaultValues, purchaseOrderState } = useContext(PurchaseOrderFormContext);
  const {
    incoterm,
    is_variable_pricing: isVariablePricing,
    seller_id: sellerId,
    company_payment_term_id: companyPaymentTermId,
  } = useWatch<CreateFormValues>();
  const isVariablePricingEnabled = useVariablePricingEnabled();
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState<PaymentTermOption | undefined | null>();
  const isCustomPaymentTerm = companyPaymentTermId === undefined;

  const { data: paymentTermsData } = useApiTradeV1TradeCompaniesPaymentTermsList(sellerId || 0, undefined, {
    query: {
      enabled: Boolean(sellerId),
    },
  });

  const getSelectedPaymentTerm = useCallback(() => {
    if (!paymentTermsData?.results || !sellerId) {
      return null;
    }

    // If we have a company_payment_term_id, find the matching term
    if (companyPaymentTermId) {
      const matchingTerm = paymentTermsData.results.find((term) => term.id === companyPaymentTermId);
      if (matchingTerm) {
        return {
          isCustom: false,
          label: createPaymentTermLabel(matchingTerm),
          term: matchingTerm,
          value: matchingTerm.id.toString(),
        };
      }
    }

    // If no company_payment_term_id, it's a custom payment term
    return {
      isCustom: true,
      label: 'Custom',
      term: null,
      value: 'custom',
    };
  }, [sellerId, paymentTermsData?.results, companyPaymentTermId]);

  const handlePaymentTermSelect = useCallback(
    (option: PaymentTermOption | null) => {
      setSelectedPaymentTerm(option);
      if (option?.term) {
        setValue('company_payment_term_id', option.term.id);
        setValue('deposit_percentage', option.term.deposit_percentage);
        if (option.term.deposit_payment_event) {
          setValue('deposit_payment_event', option.term.deposit_payment_event);
        }
        setValue('deposit_event_offset', Math.abs(option.term.deposit_event_offset_days || 0));
        setValue(
          'deposit_event_offset_direction',
          option.term.deposit_event_offset_days ? (option.term.deposit_event_offset_days >= 0 ? 1 : -1) : 1
        );
        setValue('payment_event', option.term.payment_event);
        setValue('due_date_offset', Math.abs(option.term.due_date_offset_days));
        setValue('due_date_direction', option.term.due_date_offset_days >= 0 ? 1 : -1);
        setValue('payment_terms_comments', option.term.default_payment_comment);
      } else {
        setValue('company_payment_term_id', undefined);
        setValue('deposit_percentage', '0');
        setValue('deposit_payment_event', undefined);
        setValue('deposit_event_offset', 0);
        setValue('deposit_event_offset_direction', 1);
        setValue('payment_terms_comments', '');
        setValue('payment_event', '' as PaymentEventEnum);
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (companyPaymentTermId && variant === 'update') {
      const paymentTerm = getSelectedPaymentTerm();

      handlePaymentTermSelect(paymentTerm);
    }
    // This is fine to do as we only want this to run when the paymentTermsData changes
    // so we can set the payment term that was initially set on the SO
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTermsData]);

  useEffect(() => {
    const preferred = paymentTermsData?.results.find((term) => term.is_preferred);

    if (preferred && paymentTermsData && variant === 'create') {
      handlePaymentTermSelect({
        isCustom: false,
        label: createPaymentTermLabel(preferred),
        term: preferred,
        value: preferred.id.toString(),
      });
    }
  }, [paymentTermsData, handlePaymentTermSelect, variant]);

  useResetOnChange<CreateFormValues>('payment_event', ['incoterm'], resetField);

  const paymentTermsFieldDisabled = variant === 'update' && purchaseOrderState === OrderState.PE;
  const paymentFieldsDisabled = !!sellerId && !isCustomPaymentTerm;

  return (
    <SectionContainer title="Financial details">
      {isVariablePricingEnabled ? <SwitchField name="is_variable_pricing" label="Variable pricing" /> : null}
      <PriceAndCurrencyField
        data-testid="price-per-tonne"
        label="Price per tonne"
        priceName="price_per_tonne"
        currencyLabel="Seller Currency"
        currencyName={currencyPriceName}
        required
        infoTooltip="Price per 1 tonne of net weight of the material."
      />

      <PriceAndCurrencyField
        data-testid="third-party-commission"
        label="Commission"
        priceName="third_party_commission_per_tonne"
        currencyLabel="Third Party Commission Currency"
        currencyName="third_party_commission_currency"
        infoTooltip="Commission paid to a third party or agent, calculated on per-tonne basis."
      />
      <PaymentMethod
        sx={{ gridColumn: 'span 3' }}
        required={true}
        infoTooltip="Method using which Safi will pay the seller."
      />
      <BooleanSelectField
        name="payment_before_material_is_released"
        label="Payment before material is released"
        required
        infoTooltip="If Yes, payment is required before loading can happen."
      />
      <CompanyPaymentTermsAutocomplete
        value={selectedPaymentTerm}
        name="payment_term_id"
        label="Payment Terms"
        tradeCompanyId={sellerId}
        onSelectOption={handlePaymentTermSelect}
        infoTooltip="Select predefined payment terms or choose 'Custom' to define your own."
        disabled={paymentTermsFieldDisabled}
      />
      <TextField
        type="number"
        label="Deposit Percentage upfront"
        name="deposit_percentage"
        infoTooltip="Percentage of the total amount that will be paid by Safi upfront, as a pre-payment."
        disabled={paymentFieldsDisabled}
      />
      <SelectField
        label="Deposit Payment Event"
        name="deposit_payment_event"
        options={DEPOSIT_PAYMENT_EVENT_OPTIONS}
        infoTooltip="Event that defines when the deposit will be paid."
        disabled={paymentFieldsDisabled}
      />
      <Box display="flex" flexDirection="row">
        <TextField
          type="number"
          label="Deposit Event Offset"
          name="deposit_event_offset"
          sx={{ flexShrink: 1 }}
          disabled={paymentFieldsDisabled}
        />
        <SelectField
          label=""
          data-testid="deposit_event_offset_direction"
          name="deposit_event_offset_direction"
          fullWidth={true}
          infoTooltip="Number of days before or after the payment event the payment will be made by Safi."
          options={[
            {
              label: 'Before',
              value: '-1',
            },
            {
              label: 'After',
              value: '1',
            },
          ]}
          disabled={paymentFieldsDisabled}
        />
      </Box>
      <TextField
        minRows={2}
        label="Payment terms comments"
        name="payment_terms_comments"
        infoTooltip="Number of days before or after the deposit payment event the deposit will be paid."
        disabled={paymentFieldsDisabled}
      />
      <Box display="flex" flexDirection="row">
        <TextField
          type="number"
          label="Due date offset"
          name="due_date_offset"
          sx={{ flexShrink: 1 }}
          disabled={paymentFieldsDisabled}
        />
        <SelectField
          label=""
          data-testid="due_date_direction"
          name="due_date_direction"
          fullWidth={true}
          infoTooltip="Number of days before or after the payment event the payment will be made by Safi."
          options={[
            {
              label: 'Before',
              value: '-1',
            },
            {
              label: 'After',
              value: '1',
            },
          ]}
          disabled={paymentFieldsDisabled}
        />
      </Box>
      <SelectField
        label="Payment Event"
        name="payment_event"
        options={incoterm ? getBuyerPaymentEventOptions(incoterm) : PAYMENT_EVENT_OPTIONS}
        infoTooltip="Event that defines when Safi pays the seller for the material."
        disabled={paymentFieldsDisabled}
      />
      {isVariablePricingEnabled && isVariablePricing ? (
        <VariablePricingFields defaultValues={defaultValues} formVariant={variant} />
      ) : null}
    </SectionContainer>
  );
};

const getBuyerPaymentEventOptions = (incoterm: CreateFormValues['incoterm'] | undefined) => {
  switch (incoterm) {
    case 'EXW':
    case 'FCA':
    case 'FAS':
      return [
        {
          label: 'Collection from supplier',
          value: PaymentEventEnum.CL,
        },
        {
          label: 'Export',
          value: PaymentEventEnum.XP,
        },
      ];
    case 'FOB':
      return [
        {
          label: 'Collection from supplier',
          value: PaymentEventEnum.CL,
        },
        {
          label: 'Export',
          value: PaymentEventEnum.XP,
        },
        {
          label: 'Bill of Lading',
          value: PaymentEventEnum.BL,
        },
      ];
    case 'CFR':
    case 'CIF':
      return [
        {
          label: 'Collection from supplier',
          value: PaymentEventEnum.CL,
        },
        {
          label: 'Export',
          value: PaymentEventEnum.XP,
        },
        {
          label: 'Bill of Lading',
          value: PaymentEventEnum.BL,
        },
        {
          label: 'Destination port arrival',
          value: PaymentEventEnum.DA,
        },
        {
          label: 'ICD arrival',
          value: PaymentEventEnum.IA,
        },
      ];
    default:
      return [];
  }
};
